import * as React from 'react';
import { IConcept } from 'types/notes';
import { DEFAULT_FONT_SIZE, themes } from '../utils';

interface ISearch {
  searchTerm: string;
  currentSearchPosition: number;
  totalSearchResults: number;
}

export interface IEpubContext {
  fontSize: number;
  searchObj: ISearch;
  currentNotesUID: string;
  selectedConcept: IConcept;
  disableNavigation: boolean;
  onDisableNavigation: (disable: boolean) => void;
  onChangeSelectedConcept: (concept: IConcept) => void;
  onChangeSearchResults: (results: number) => void;
  onSearchChange: (searchTerm: string) => void;
  onFontSizeChange: (fontSize: number) => void;
  onNavigation: (direction: string) => void;
  onChangeSearchPosition: (position: number) => void;
}

export const EpubContext = React.createContext<IEpubContext>(null);

interface IEpubContextProviderProps {
  children: React.ReactNode;
  currentNotesUID: string;
  initialSelectedConcept: IConcept;
}

export const EpubContextProvider: React.FC<IEpubContextProviderProps> = ({
  children,
  currentNotesUID,
  initialSelectedConcept
}) => {
  const [selectedConcept, setSelectedConcept] = React.useState(
    initialSelectedConcept
  );

  const [disableNavigation, setDisableNavigation] = React.useState(false);

  const [fontSize, setFontSize] = React.useState(DEFAULT_FONT_SIZE);

  const [searchObj, setSearchObj] = React.useState({
    searchTerm: '',
    totalSearchResults: 0,
    currentSearchPosition: 1
  });

  const onChangeSearchPosition = (position) =>
    setSearchObj({ ...searchObj, currentSearchPosition: position });

  const onChangeSearchResults = (totalSearchResults) =>
    setSearchObj({ ...searchObj, totalSearchResults });

  const onSearchChange = (searchTerm) =>
    setSearchObj({ ...searchObj, searchTerm });

  const onFontSizeChange = (fontSize) => setFontSize(fontSize);

  const onChangeSelectedConcept = (concept) => {
    setSelectedConcept(concept);
  };

  const onNavigation = (direction) => {
    const updatedSearchPosition =
      direction === 'up' && searchObj.currentSearchPosition > 0
        ? searchObj.currentSearchPosition - 1
        : searchObj.currentSearchPosition + 1;

    setSearchObj({
      ...searchObj,
      currentSearchPosition: updatedSearchPosition
    });
  };

  const onDisableNavigation = (disable) => setDisableNavigation(disable);

  const data = {
    selectedConcept,
    fontSize,
    searchObj,
    currentNotesUID,
    disableNavigation,
    onNavigation,
    onSearchChange,
    onFontSizeChange,
    onChangeSearchResults,
    onChangeSelectedConcept,
    onChangeSearchPosition,
    onDisableNavigation
  };

  return <EpubContext.Provider value={data}>{children}</EpubContext.Provider>;
};
